/**
 * ChildFilterCards — Shared child selector cards used in TimeValidation and Payroll.
 * Displays "Tous les enfants" + one card per child with gender-aware styling.
 *
 * Props:
 *   children      — array of { contract_id, child_first_name, child_sex, ...stats }
 *   selectedChild — 'all' or a contract_id
 *   onSelect      — (contractId | 'all') => void
 *   allLabel      — label for the "Tous" card (default "Tous les enfants")
 *   allSublabel   — sublabel for the "Tous" card (e.g. "12 entrées")
 *   renderStats   — (child) => ReactNode — render extra badges/stats per child card
 */
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChildIcon, genderBg, genderColor, genderBorder } from './ChildIcon';

export const ChildFilterCards = ({
  children,
  selectedChild,
  onSelect,
  allLabel = 'Tous les enfants',
  allSublabel,
  renderStats,
}) => {
  if (!children || children.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="child-filter-cards">
      {/* "Tous" card — only when multiple children */}
      {children.length > 1 && (
        <Card
          className={cn(
            "cursor-pointer transition-all duration-200 hover:shadow-md",
            selectedChild === 'all' && "ring-2 ring-[var(--primary-400)] border-[var(--primary-400)] shadow-md",
          )}
          onClick={() => onSelect('all')}
          data-testid="child-filter-all"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[var(--primary-100)]">
                <Calendar className="h-5 w-5 text-[var(--primary-400)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{allLabel}</p>
                {allSublabel && (
                  <p className="text-xs text-[var(--text-tertiary)]">{allSublabel}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-child cards */}
      {children.map(child => {
        const isSelected = selectedChild === child.contract_id;

        return (
          <Card
            key={child.contract_id}
            className={cn(
              "cursor-pointer transition-all duration-200 hover:shadow-md",
              isSelected && "ring-2 shadow-md",
            )}
            style={isSelected ? { borderColor: genderBorder(child.child_sex), '--tw-ring-color': genderBorder(child.child_sex) } : {}}
            onClick={() => onSelect(selectedChild === child.contract_id ? 'all' : child.contract_id)}
            data-testid={`child-filter-${child.contract_id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: genderBg(child.child_sex) }}
                >
                  <ChildIcon sex={child.child_sex} className="h-5 w-5" style={{ color: genderColor(child.child_sex) }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                    {child.child_first_name || child.child_name}
                  </p>
                  {renderStats ? (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {renderStats(child)}
                    </div>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
